import React, { useEffect, useMemo, useRef, useState } from 'react'
import Stack from '@mui/material/Stack';
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MobileStepper, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { requiresEmailVerification } from '../authVerification';
import { useData } from '../DataProviderContext';
import ContextualEarlyAccessNotice from '../ContextualEarlyAccessNotice';
import { MyAlert } from '../MyAlert';
import JourneyListView from './JourneyListView'
import {
    JOURNEY_CONTEXT_MAX_LENGTH,
    JOURNEY_STATE_MAX_LENGTH,
    GOAL_FOCUS_MAX_WORDS,
    countGoalFocusWords,
    getJourneyDetailsValidation,
    getJourneyFramingValidation,
    normalizeJourneyData,
} from '../storage/actionItemModel';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { LockedButton } from '../SignedInLock';

const CLOUD_SAVE_NOTICE_ACCEPTED_KEY = 'journeybot_cloud_save_notice_accepted';

const formatSavedAt = (value) => {
    if (!value) {
        return 'Unknown';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'Unknown';
    }

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
};

const getVersionRecency = (localRecord, cloudRecord) => {
    const localTime = localRecord?.lastLocalSaveAt ? new Date(localRecord.lastLocalSaveAt).getTime() : null;
    const cloudTime = cloudRecord?.lastCloudSaveAt ? new Date(cloudRecord.lastCloudSaveAt).getTime() : null;
    const safeLocalTime = Number.isFinite(localTime) ? localTime : null;
    const safeCloudTime = Number.isFinite(cloudTime) ? cloudTime : null;

    if (safeLocalTime === null || safeCloudTime === null) {
        return 'unknown';
    }

    if (safeLocalTime > safeCloudTime) {
        return 'local';
    }

    if (safeCloudTime > safeLocalTime) {
        return 'cloud';
    }

    return 'same';
};


/* 
WHAT is the initial state of JourneyView
- initial states
- load the selected user journey
- load the collaborated user journey

WHEN Does JourneyView get re-rendered
- on addition of new cards
- on deletion of cards
- on reorder* of cards
- on refres of collaborated user Journey

*/
function GroupedTasks({ onOpenAiChat }) {
    // console.log('Journeyview: START');
    const { authState } = useAuth();
    const {
        dataState,
        exportJourneyJson,
        importJourneyJson,
        loadMyDocuments,
        resolveCloudChoice,
        saveLocalWork,
        saveJourneyDetails,
        saveMyWork,
        selectGoalFocus,
        dispatch
        , startOverGuestJourney
    } = useData();
    const [open, setOpen] = useState(false);
    const [cloudSaveNoticeOpen, setCloudSaveNoticeOpen] = useState(false);
    const [guestSaveOpen, setGuestSaveOpen] = useState(false);
    const [startOverOpen, setStartOverOpen] = useState(false);
    const [navigationWarning, setNavigationWarning] = useState({ open: false, href: '' });
    const navigate = useNavigate();
    const location = useLocation();
    const loadAttemptedForUser = useRef(null);
    const sliderRef = useRef(null);
    const importInputRef = useRef(null);
    const [activeStep, setActiveStep] = useState(0);
    const [desktopSlide, setDesktopSlide] = useState(0);
    const [framingTouched, setFramingTouched] = useState(false);
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [isSavingDetails, setIsSavingDetails] = useState(false);
    const [detailsErrors, setDetailsErrors] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const desktopVisibleCards = isTablet ? 1 : 2;

    var settings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 2,
        slidesToScroll: 2,
        afterChange: (index) => setDesktopSlide(index),
        responsive: [
            {
                breakpoint: 900,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };




    var objective = "";
    var objectiveDescription = "";


    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSaveToCloudClick = () => {
        if (localStorage.getItem(CLOUD_SAVE_NOTICE_ACCEPTED_KEY) === 'true') {
            saveMyWork();
            return;
        }

        setCloudSaveNoticeOpen(true);
    };

    const handleKeepLocalOnly = () => {
        setCloudSaveNoticeOpen(false);
    };

    const handleAcceptCloudSaveNotice = () => {
        localStorage.setItem(CLOUD_SAVE_NOTICE_ACCEPTED_KEY, 'true');
        setCloudSaveNoticeOpen(false);
        saveMyWork();
    };


    // console.log('Context Provider Initial values');
    // console.log('data provider context :' + JSON.stringify(dataState));

    useEffect(() => {
        if (dataState.guestJourneyStatus) return;
        const userId = authState.user?.uid;
        const canUseCloud = Boolean(userId && !requiresEmailVerification(authState.user));
        const localOwnerKey = `${userId || 'anonymous'}:${canUseCloud ? 'cloud' : 'local'}`;

        // Authentication may resolve after an anonymous/local working copy has loaded.
        // Reconcile once for every owner/access transition even when data is already open.
        if (loadAttemptedForUser.current !== localOwnerKey) {
            loadAttemptedForUser.current = localOwnerKey;
            if (canUseCloud) {
                const activeJourneyId = dataState.activeGoal?.journeyId;
                const activeLocalId = `${userId}:${activeJourneyId}`;
                const hasLoadedActiveJourney = Boolean(
                    dataState.dataLoaded
                    && activeJourneyId
                    && activeJourneyId !== 'default'
                    && dataState.journeyMeta?.id === activeLocalId
                );
                if (!hasLoadedActiveJourney) {
                    loadMyDocuments();
                }
            } else if (!dataState.suppressAnonymousLoad) {
                selectGoalFocus(dataState.activeGoal);
            }
        }
    }, [authState.user, authState.user?.emailVerified, dataState.activeGoal, dataState.dataLoaded, dataState.guestJourneyStatus, dataState.journeyMeta?.id, dataState.suppressAnonymousLoad, loadMyDocuments, selectGoalFocus]);

    const steps = useMemo(
        () => (Array.isArray(dataState?.userDocs?.steps) ? dataState.userDocs.steps : []),
        [dataState?.userDocs?.steps]
    );
    const totalCards = steps.length;
    const safeActiveStep = Math.min(activeStep, Math.max(totalCards - 1, 0));
    const hasMultipleCards = totalCards > 1;
    const hasDesktopPagination = totalCards > desktopVisibleCards;
    const isFirstCard = safeActiveStep === 0;
    const isLastCard = safeActiveStep >= totalCards - 1;
    const safeDesktopSlide = Math.min(desktopSlide, Math.max(totalCards - 1, 0));
    const desktopFirstVisible = totalCards === 0 ? 0 : safeDesktopSlide + 1;
    const desktopLastVisible = Math.min(safeDesktopSlide + desktopVisibleCards, totalCards);
    const desktopCanGoBack = safeDesktopSlide > 0;
    const desktopCanGoNext = desktopLastVisible < totalCards;
    const isSignedIn = Boolean(authState.user);
    const canUseCloud = Boolean(authState.user && !requiresEmailVerification(authState.user));
    const isGuestPreview = dataState.guestJourneyStatus === 'preview';
    const isGuestUnsaved = dataState.guestJourneyStatus === 'unsaved';
    const isGuestTransient = Boolean(dataState.guestJourneyStatus);
    const storageStatusLabel = dataState.storageStatus || 'Saved locally';
    const hasCloudConflict = Boolean(dataState.cloudConflict);
    const hasLocalConflictVersion = Boolean(dataState.cloudConflict?.localRecord);
    const localConflictRecord = dataState.cloudConflict?.localRecord;
    const cloudConflictRecord = dataState.cloudConflict?.cloudRecord;
    const conflictRecency = getVersionRecency(localConflictRecord, cloudConflictRecord);
    const journeyData = useMemo(
        () => normalizeJourneyData(dataState?.userDocs || {}),
        [dataState?.userDocs]
    );
    const framingErrors = useMemo(
        () => getJourneyFramingValidation(journeyData, { requireRequiredFields: framingTouched }),
        [framingTouched, journeyData]
    );
    const hasFramingErrors = Object.keys(framingErrors).length > 0;
    const savedGoalFocus = dataState?.journeyMeta?.title || dataState?.activeGoal?.goal || '';
    const [goalFocusDraft, setGoalFocusDraft] = useState(savedGoalFocus);
    const [contextDraft, setContextDraft] = useState(journeyData.context);
    const goalFocusWordCount = countGoalFocusWords(goalFocusDraft);
    const hasUnsavedDetails = isEditingDetails
        && (goalFocusDraft !== savedGoalFocus || contextDraft !== journeyData.context);

    const beginDetailsEdit = () => {
        setGoalFocusDraft(savedGoalFocus);
        setContextDraft(journeyData.context);
        setDetailsErrors({});
        setIsEditingDetails(true);
    };

    const cancelDetailsEdit = () => {
        setGoalFocusDraft(savedGoalFocus);
        setContextDraft(journeyData.context);
        setDetailsErrors({});
        setIsEditingDetails(false);
    };

    const handleSaveDetails = async () => {
        const errors = getJourneyDetailsValidation({ goalFocus: goalFocusDraft, context: contextDraft });
        setDetailsErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsSavingDetails(true);
        try {
            await saveJourneyDetails({ goalFocus: goalFocusDraft, context: contextDraft });
            setGoalFocusDraft(goalFocusDraft.trim());
            setIsEditingDetails(false);
        } catch {
            // The provider displays the existing alert treatment. Keep the draft available for retry.
        } finally {
            setIsSavingDetails(false);
        }
    };

    useEffect(() => {
        if (activeStep > 0 && activeStep >= totalCards) {
            setActiveStep(Math.max(totalCards - 1, 0));
        }
    }, [activeStep, totalCards]);

    useEffect(() => {
        if (desktopSlide > 0 && desktopSlide >= totalCards) {
            setDesktopSlide(Math.max(totalCards - 1, 0));
        }
    }, [desktopSlide, totalCards]);

    useEffect(() => {
        setActiveStep(0);
        setDesktopSlide(0);
        setFramingTouched(false);
        setIsEditingDetails(false);
        setDetailsErrors({});
        sliderRef.current?.slickGoTo?.(0);
    }, [dataState?.journeyMeta?.id]);

    useEffect(() => {
        if (!hasUnsavedDetails) {
            return undefined;
        }

        const message = 'You have unsaved journey detail changes. Leave without saving?';
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        const handleDocumentClick = (event) => {
            const navigationControl = event.target.closest?.('a[href], [data-journey-navigation="true"]');
            if (navigationControl && !window.confirm(message)) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleDocumentClick, true);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleDocumentClick, true);
        };
    }, [hasUnsavedDetails]);

    useEffect(() => {
        if (!isGuestTransient) return undefined;
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        const handleDocumentClick = (event) => {
            if (!isGuestPreview) return;
            const link = event.target.closest?.('a[href]');
            if (!link || link.target === '_blank' || link.href === window.location.href) return;
            event.preventDefault();
            event.stopPropagation();
            setNavigationWarning({ open: true, href: link.href });
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('click', handleDocumentClick, true);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('click', handleDocumentClick, true);
        };
    }, [isGuestPreview, isGuestTransient]);

    const signInToSave = () => {
        setGuestSaveOpen(false);
        setNavigationWarning({ open: false, href: '' });
        navigate('/login', {
            state: { from: { pathname: location.pathname, search: location.search }, guestJourney: true },
        });
    };

    const confirmStartOver = () => {
        startOverGuestJourney();
        setStartOverOpen(false);
        onOpenAiChat?.();
    };

    const leaveWithoutSaving = () => {
        const href = navigationWarning.href;
        startOverGuestJourney();
        setNavigationWarning({ open: false, href: '' });
        if (href) window.location.assign(href);
    };

    const goToPreviousStep = () => {
        if (!isFirstCard) {
            setActiveStep((current) => Math.max(current - 1, 0));
        }
    };

    const goToNextStep = () => {
        if (!isLastCard) {
            setActiveStep((current) => Math.min(current + 1, totalCards - 1));
        }
    };

    const goToPreviousDesktopCards = () => {
        if (desktopCanGoBack) {
            sliderRef.current?.slickPrev();
        }
    };

    const goToNextDesktopCards = () => {
        if (desktopCanGoNext) {
            sliderRef.current?.slickNext();
        }
    };

    // console.log('Journeyview: END Right before Render');

    if (dataState) {
        // console.log('Loaded Journey data is :' + JSON.stringify(dataState.userDocs));
        // console.log('steps in the data are  :' + JSON.stringify(dataState.userDocs.steps));
    }

    // avoid for now
    // should render when journeyhas been loaded or modified
    if (dataState) {
        // console.log('Journey Has been  loaded' + dataState.userDocs);

        // card action // votes counter? 
        // Rating MUI component with number of votes
        // FUTURE discussion thread opens a popup modal where you input your thought
        // FUTURE AI summarises that and checks agains all others to see if there is an exsiting similar point

        try {
            var lists;
            console.log('GROUPTASK: LISTS');
            lists = steps.map((item, index) => {
                // console.log('ITEM:' + JSON.stringify(item));
                return (
                    <JourneyListView key={`${dataState?.journeyMeta?.id || 'journey'}-${item?.step_id ?? index}`} listroot={index}></JourneyListView>

                );
            })

            console.log(lists);
            console.log('GROUPTASK: LISTS');

        } catch (error) {
            console.log('Error here ' + error);
        }
    }

    return (
        <Box data-testid="grouped-tasks" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'clip' }}>
            {dataState.showAlert === true ? (
                <MyAlert
                    message={dataState.alertMessage}
                    action={hasCloudConflict ? (
                        <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            onClick={() => resolveCloudChoice('cloud')}
                            data-testid="load-cloud-from-alert"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Load cloud version
                        </Button>
                    ) : null}
                />
            ) : null}

            <Stack spacing={{ xs: 3, md: 4 }}>



                {
                    /*
                        TODO : Make this title editable using the edit icon
                        tasks: 
                        make Edit a button
                        have on click
                        in place text edit , similar to goal editor
                        
                        when in edit mode
                        on save will update the document
                        dispatch({ type: 'UPDATE_TITLE', payload: {   newName: objective, description: descDescription, itemid:  } });

                        in data context
                        if item does not have an id: add one
                        else find item in object and update it

                        goal id


                    */
                }

                <Card
                    component="section"
                    aria-labelledby="current-journey-heading"
                    variant="outlined"
                    data-testid="current-journey-panel"
                    sx={{ borderRadius: 2, borderTop: 4, borderTopColor: 'primary.main', boxShadow: 1, bgcolor: 'background.paper' }}
                >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, sm: 3.5, md: 4 } } }}>
                        <Stack spacing={3}>
                            <Box data-testid="current-journey-row" sx={{ width: '100%', minWidth: 0 }}>
                                <Box sx={{ minWidth: 0, width: '100%' }}>
                                    <Stack spacing={1.5} sx={{ mt: 1, width: '100%' }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                                                    Current journey
                                                </Typography>
                                                {!isEditingDetails ? (
                                                    <Typography
                                                        id="current-journey-heading"
                                                        variant="h4"
                                                        component="h2"
                                                        sx={{ maxWidth: '24ch', fontWeight: 850, lineHeight: 1.2, letterSpacing: '-0.015em', overflowWrap: 'anywhere' }}
                                                        data-testid="journey-goal-display"
                                                    >
                                                        {savedGoalFocus || 'Untitled journey'}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                            {!isEditingDetails ? (isGuestPreview ? (
                                                <LockedButton
                                                    type="button"
                                                    variant="outlined"
                                                    startIcon={<EditOutlinedIcon />}
                                                    data-testid="edit-journey-details"
                                                >
                                                    Edit
                                                </LockedButton>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    startIcon={<EditOutlinedIcon />}
                                                    onClick={beginDetailsEdit}
                                                    aria-label="Edit journey details"
                                                    data-testid="edit-journey-details"
                                                >
                                                    Edit
                                                </Button>
                                            )) : null}
                                        </Stack>

                                        {isEditingDetails ? (
                                            <>
                                                <TextField
                                                    label="Goal focus"
                                                    value={goalFocusDraft}
                                                    onChange={(event) => {
                                                        setGoalFocusDraft(event.target.value);
                                                        setDetailsErrors((current) => ({ ...current, goalFocus: undefined }));
                                                    }}
                                                    error={Boolean(detailsErrors.goalFocus)}
                                                    helperText={detailsErrors.goalFocus || `${goalFocusWordCount} / ${GOAL_FOCUS_MAX_WORDS} words`}
                                                    required
                                                    fullWidth
                                                    autoFocus
                                                    data-testid="goal-focus-input"
                                                />
                                                <TextField
                                                    label="Journey context"
                                                    value={contextDraft}
                                                    onChange={(event) => {
                                                        setContextDraft(event.target.value);
                                                        setDetailsErrors((current) => ({ ...current, context: undefined }));
                                                    }}
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    inputProps={{ maxLength: JOURNEY_CONTEXT_MAX_LENGTH }}
                                                    error={Boolean(detailsErrors.context)}
                                                    helperText={detailsErrors.context || `${contextDraft.length} / ${JOURNEY_CONTEXT_MAX_LENGTH}`}
                                                    data-testid="journey-context-input"
                                                />
                                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                                    <Button
                                                        type="button"
                                                        variant="contained"
                                                        onClick={handleSaveDetails}
                                                        disabled={isSavingDetails}
                                                        data-testid="save-journey-details"
                                                    >
                                                        {isSavingDetails ? 'Saving…' : 'Save'}
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        onClick={cancelDetailsEdit}
                                                        disabled={isSavingDetails}
                                                        data-testid="cancel-journey-details"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Stack>
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    maxWidth: '72ch',
                                                    px: { xs: 2, sm: 2.5 },
                                                    py: 1.75,
                                                    bgcolor: 'action.hover',
                                                    borderLeft: 4,
                                                    borderLeftColor: 'primary.main',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 800 }}>
                                                    Journey context
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }} data-testid="journey-context-display">
                                                    {journeyData.context || 'No journey context added.'}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Divider />

                                        <Box
                                            data-testid="journey-framing-grid"
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                                                gap: 2,
                                                alignItems: 'start',
                                            }}
                                        >
                                            <TextField
                                                label="Where are you starting?"
                                                value={journeyData.startState}
                                                onChange={(event) => {
                                                    setFramingTouched(true);
                                                    dispatch({ type: 'UPDATE_JOURNEY_FRAMING', payload: { startState: event.target.value } });
                                                }}
                                                required
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                inputProps={{ maxLength: JOURNEY_STATE_MAX_LENGTH }}
                                                error={Boolean(framingErrors.startState)}
                                                helperText={framingErrors.startState || 'Define the current state this Journey moves from.'}
                                                data-testid="journey-start-state-input"
                                                InputProps={{ readOnly: isGuestPreview }}
                                                aria-readonly={isGuestPreview ? 'true' : undefined}
                                            />
                                            <TextField
                                                label="What does the end of this Journey look like?"
                                                value={journeyData.endState}
                                                onChange={(event) => {
                                                    setFramingTouched(true);
                                                    dispatch({ type: 'UPDATE_JOURNEY_FRAMING', payload: { endState: event.target.value } });
                                                }}
                                                required
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                inputProps={{ maxLength: JOURNEY_STATE_MAX_LENGTH }}
                                                error={Boolean(framingErrors.endState)}
                                                helperText={framingErrors.endState || 'Describe the outcome that means this Journey is complete.'}
                                                data-testid="journey-end-state-input"
                                                InputProps={{ readOnly: isGuestPreview }}
                                                aria-readonly={isGuestPreview ? 'true' : undefined}
                                            />
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>

                            <Stack
                                data-testid="journey-controls-row"
                                direction="row"
                                spacing={1}
                                sx={{
                                    p: { xs: 1.75, sm: 2 },
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                    minWidth: 0,
                                    bgcolor: 'action.hover',
                                    borderRadius: 1.5,
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
                                    {isGuestPreview ? (
                                        <LockedButton variant="contained" color="primary" startIcon={<AddIcon />} size="large" data-testid="add-step" sx={{ minHeight: 44 }}>Add Step</LockedButton>
                                    ) : (
                                        <Button variant="contained" color="primary" onClick={handleClickOpen} startIcon={<AddIcon />} size="large" data-testid="add-step" sx={{ minHeight: 44 }}>Add Step</Button>
                                    )}
                                    <Chip
                                        label={storageStatusLabel}
                                        variant="outlined"
                                        color="primary"
                                        sx={{ justifyContent: 'center', fontWeight: 700, maxWidth: '100%' }}
                                    />
                                </Stack>
                                    {!canUseCloud ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280, overflowWrap: 'anywhere' }}>
                                            {isSignedIn
                                                ? 'Verify your email to save or load cloud journeys. This journey remains saved in this browser.'
                                                : 'This journey is saved in this browser. Sign in to save or load cloud journeys.'}
                                        </Typography>
                                    ) : null}
                            </Stack>

                            <Divider />

                            <ContextualEarlyAccessNotice />

                            {hasCloudConflict ? (
                                <Stack spacing={1.25} sx={{ p: 1.5, border: 1, borderColor: 'warning.main', borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.08)' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                        Choose which journey to use
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {hasLocalConflictVersion
                                            ? 'Local and cloud data are both present. Nothing will be overwritten unless you choose Save to cloud later.'
                                            : 'A cloud journey is available for this account. Load it to use the same journey on this browser.'}
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                        {hasLocalConflictVersion ? (
                                            <Box sx={{ flex: 1, p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                                    Local version {conflictRecency === 'local' ? '(newer)' : ''}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Last saved locally: {formatSavedAt(localConflictRecord?.lastLocalSaveAt)}
                                                </Typography>
                                            </Box>
                                        ) : null}
                                        <Box sx={{ flex: 1, p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                                Cloud version {conflictRecency === 'cloud' ? '(newer)' : ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Last saved to cloud: {formatSavedAt(cloudConflictRecord?.lastCloudSaveAt)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    {conflictRecency === 'same' ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Both versions have the same saved time.
                                        </Typography>
                                    ) : null}
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        {hasLocalConflictVersion ? (
                                            <Button variant="outlined" color="primary" onClick={() => resolveCloudChoice('local')}>
                                                Use local version
                                            </Button>
                                        ) : null}
                                        <Button variant="outlined" color="primary" onClick={() => resolveCloudChoice('cloud')}>
                                            Use cloud version
                                        </Button>
                                        {hasLocalConflictVersion ? (
                                            <Button variant="outlined" color="primary" onClick={() => resolveCloudChoice('both')}>
                                                Keep both / duplicate
                                            </Button>
                                        ) : null}
                                    </Stack>
                                </Stack>
                            ) : null}

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                aria-label="Journey save and file controls"
                                sx={{
                                    p: { xs: 1.75, sm: 2 },
                                    alignItems: { sm: 'center' },
                                    flexWrap: 'wrap',
                                    rowGap: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1.5,
                                    bgcolor: 'grey.50',
                                    '& .MuiButton-root': { minHeight: 44, width: { xs: '100%', sm: 'auto' } },
                                }}
                            >
                                {isGuestPreview ? (
                                    <Button variant="contained" color="primary" onClick={() => setGuestSaveOpen(true)} startIcon={<SaveOutlinedIcon />} data-testid="save-guest">Save</Button>
                                ) : isGuestUnsaved ? (
                                    <Button variant="contained" color="primary" onClick={handleSaveToCloudClick} startIcon={<SaveOutlinedIcon />} disabled={hasCloudConflict || hasFramingErrors || isEditingDetails} data-testid="save-cloud">Save</Button>
                                ) : (
                                    <>
                                        <Button variant="outlined" color="primary" onClick={saveLocalWork} startIcon={<SaveOutlinedIcon />} disabled={hasFramingErrors || isEditingDetails} data-testid="save-local">Save locally</Button>
                                        {canUseCloud ? <Button variant="contained" color="primary" onClick={handleSaveToCloudClick} startIcon={<SaveOutlinedIcon />} disabled={hasCloudConflict || hasFramingErrors || isEditingDetails} data-testid="save-cloud">Save to cloud</Button> : null}
                                    </>
                                )}

                                <Button variant="outlined" color="primary" onClick={exportJourneyJson} startIcon={<FileDownloadOutlinedIcon />} data-testid="export-json">
                                    Export JSON
                                </Button>

                                {isGuestPreview ? <LockedButton variant="outlined" color="primary" startIcon={<ShareOutlinedIcon />} data-testid="share-journey">Share</LockedButton> : null}

                                {isGuestTransient ? (
                                    <>
                                        <Button variant="outlined" onClick={onOpenAiChat} startIcon={<ChatOutlinedIcon />} data-testid="reopen-creation-transcript">Creation transcript</Button>
                                        <Button variant="outlined" color="error" onClick={() => setStartOverOpen(true)} startIcon={<ReplayOutlinedIcon />} data-testid="start-over-guest">Start over</Button>
                                    </>
                                ) : null}

                                {!isGuestTransient ? <Button variant="outlined" color="primary" onClick={() => importInputRef.current?.click()} startIcon={<FileUploadOutlinedIcon />} data-testid="import-json">
                                    Import JSON
                                </Button> : null}

                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept="application/json,.json"
                                    hidden
                                    data-testid="import-json-input"
                                    onChange={(event) => {
                                        const file = event.target.files?.[0];
                                        importJourneyJson(file);
                                        event.target.value = '';
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Box component="section" aria-labelledby="journey-steps-heading" data-testid="journey-steps-section">
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ mb: 2.5, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
                    >
                        <Box>
                            <Typography id="journey-steps-heading" variant="h4" component="h2" sx={{ fontWeight: 850, letterSpacing: '-0.015em' }}>
                                Journey steps
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Work through each stage and keep the next useful actions visible.
                            </Typography>
                        </Box>
                        <Chip
                            label={`${totalCards} ${totalCards === 1 ? 'step' : 'steps'}`}
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                        />
                    </Stack>
                    {totalCards === 0 ? (
                        <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed', bgcolor: 'background.paper' }}>
                            <CardContent>
                                <Stack spacing={2} sx={{ py: { xs: 4, sm: 6 }, alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                    <Typography variant="h5" component="h3" sx={{ fontWeight: 850 }}>
                                        Start with one step.
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
                                        You can add more, import an AI plan, or build your journey as you learn.
                                    </Typography>
                                    <Button variant="contained" color="primary" onClick={handleClickOpen} startIcon={<AddIcon />} data-testid="add-step-empty" sx={{ minHeight: 44 }}>
                                        Add Step
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    ) : null}

                    {isMobile && hasMultipleCards ? (
                        <Stack
                            spacing={1.25}
                            sx={{
                                mb: 2,
                                p: 1.25,
                                border: 1,
                                borderColor: 'primary.light',
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                boxShadow: 3,
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    aria-label="Previous card"
                                    onClick={goToPreviousStep}
                                    disabled={isFirstCard}
                                    startIcon={<ArrowBackIosNewIcon />}
                                    sx={{
                                        minHeight: 48,
                                        minWidth: 112,
                                        bgcolor: 'background.paper',
                                        boxShadow: 1,
                                        opacity: isFirstCard ? 0.35 : 1,
                                        '&:hover': { bgcolor: 'background.paper' },
                                    }}
                                >
                                    Previous
                                </Button>

                                <Box sx={{ minWidth: 84, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Viewing
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        Step {safeActiveStep + 1} of {totalCards}
                                    </Typography>
                                </Box>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    aria-label="Next card"
                                    onClick={goToNextStep}
                                    disabled={isLastCard}
                                    endIcon={<ArrowForwardIosIcon />}
                                    sx={{
                                        minHeight: 48,
                                        minWidth: 112,
                                        boxShadow: 2,
                                        opacity: isLastCard ? 0.35 : 1,
                                    }}
                                >
                                    Next
                                </Button>
                            </Stack>

                            <MobileStepper
                                variant="dots"
                                steps={totalCards}
                                position="static"
                                activeStep={safeActiveStep}
                                backButton={<Box />}
                                nextButton={<Box />}
                                sx={{
                                    justifyContent: 'center',
                                    bgcolor: 'transparent',
                                    p: 0,
                                    '& .MuiMobileStepper-dot': {
                                        width: 9,
                                        height: 9,
                                    },
                                }}
                            />

                            {isFirstCard ? (
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                    Use Back and Next to move through your journey steps
                                </Typography>
                            ) : null}
                        </Stack>
                    ) : null}

                    {isMobile && totalCards > 0 ? (
                            <Box data-testid="mobile-card-layout" sx={{ mx: { xs: -0.5, sm: 0 }, pb: 2 }}>
                            {totalCards > 0 ? <JourneyListView listroot={safeActiveStep}></JourneyListView> : null}
                        </Box>
                    ) : totalCards > 0 ? (
                        <>
                            {hasDesktopPagination ? (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        mb: 1.5,
                                        p: 1,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'background.paper',
                                        boxShadow: 1,
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        aria-label="Previous card"
                                        onClick={goToPreviousDesktopCards}
                                        disabled={!desktopCanGoBack}
                                        startIcon={<ArrowBackIosNewIcon />}
                                    >
                                        Previous
                                    </Button>

                                    <Typography variant="body2" sx={{ fontWeight: 800, minWidth: 150, textAlign: 'center' }}>
                                        Showing {desktopFirstVisible}-{desktopLastVisible} of {totalCards}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        aria-label="Next card"
                                        onClick={goToNextDesktopCards}
                                        disabled={!desktopCanGoNext}
                                        endIcon={<ArrowForwardIosIcon />}
                                    >
                                        Next
                                    </Button>
                                </Stack>
                            ) : null}

                            <Box
                                data-testid="desktop-card-layout"
                                sx={{
                                    mx: { xs: -0.5, sm: 0 },
                                    pb: 2,
                                    '& .slick-list': { overflow: 'hidden' },
                                    '& .slick-slider': { width: '100%', maxWidth: '100%' },
                                    '& .slick-slide > div': { px: { xs: 0.5, sm: 1 } },
                                    '& .slick-dots': { bottom: -12 },
                                    '& .slick-dots li button:before': { color: 'primary.main', fontSize: 9 },
                                }}
                            >
                                <Slider ref={sliderRef} {...settings}>
                                    {lists}
                                </Slider>
                            </Box>
                        </>
                    ) : null}
                </Box>

                <Dialog open={guestSaveOpen} onClose={() => setGuestSaveOpen(false)} aria-labelledby="guest-save-title">
                    <DialogTitle id="guest-save-title">Save your journey</DialogTitle>
                    <DialogContent><Typography>Sign in to save this journey, edit its steps and continue with AI.</Typography></DialogContent>
                    <DialogActions>
                        <Button onClick={() => setGuestSaveOpen(false)}>Keep exploring</Button>
                        <Button variant="contained" onClick={signInToSave} startIcon={<KeyOutlinedIcon />}>Sign in to save</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={startOverOpen} onClose={() => setStartOverOpen(false)} aria-labelledby="start-over-title">
                    <DialogTitle id="start-over-title">Start over?</DialogTitle>
                    <DialogContent><Typography>The temporary conversation and journey will be permanently cleared, and a fresh guest attempt will begin.</Typography></DialogContent>
                    <DialogActions>
                        <Button onClick={() => setStartOverOpen(false)}>Keep journey</Button>
                        <Button color="error" variant="contained" onClick={confirmStartOver}>Start over</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={navigationWarning.open} onClose={() => setNavigationWarning({ open: false, href: '' })} aria-labelledby="leave-guest-title">
                    <DialogTitle id="leave-guest-title">This journey is not saved</DialogTitle>
                    <DialogContent><Typography>Sign in and save it, or leave and permanently clear the temporary journey.</Typography></DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={leaveWithoutSaving}>Leave without saving</Button>
                        <Button variant="contained" onClick={signInToSave} startIcon={<KeyOutlinedIcon />}>Sign in to save</Button>
                    </DialogActions>
                </Dialog>






                {/* Dialog component */} <Dialog open={open} onClose={handleClose}
                    fullScreen={isMobile}
                    fullWidth
                    maxWidth={'md'}
                    PaperProps={{
                        component: 'form',
                        onSubmit: (event) => {
                            // invoke firebase here?
                            // put in Quuee to submit
                            event.preventDefault();
                            const formData = new FormData(event.currentTarget);
                            const formJson = Object.fromEntries(formData.entries());
                            const objective = formJson.objective;
                            const descDescription = formJson.objectiveDescription;
                            console.log(objective);
                            dispatch({ type: 'NEW_TASK', payload: { newName: objective, description: descDescription } });

                            //updateClientSideJourney(selectedStep, suggestion);
                            handleClose();
                        },
                    }}>
                    <DialogTitle>Add a journey step</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Add the next meaningful step in your first-home journey.
                        </Typography>
                    </DialogContent>
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="name"
                        name="objective"
                        data-testid="new-step-title-input"
                        label="Step title"
                        type="text"
                        fullWidth
                        multiline
                        rows={1}
                        variant="outlined"
                        defaultValue={objective ? objective : ''}
                    />
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="name"
                        name="objectiveDescription"
                        data-testid="new-step-description-input"
                        label="Brief description"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        defaultValue={objectiveDescription ? objectiveDescription : ''}
                    />
                    <DialogActions>
                        <Button type="submit" data-testid="save-new-step">Save</Button>
                        <Button onClick={handleClose} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={cloudSaveNoticeOpen}
                    onClose={handleKeepLocalOnly}
                    fullWidth
                    maxWidth="sm"
                    data-testid="cloud-save-notice-dialog"
                >
                    <DialogTitle>Save this journey to cloud?</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <ContextualEarlyAccessNotice />
                            <Typography variant="body2">
                                JourneyBot can store your journey data in cloud storage so you can reload it after login and use it across devices.
                            </Typography>
                            <Typography variant="body2">
                                Your journey may include goals, notes, decisions, blockers, action items, and AI-generated content that you choose to import.
                            </Typography>
                            <Typography variant="body2">
                                JourneyBot is a planning workspace, not a professional advice service. Do not save information you are not comfortable storing under our Privacy Policy.
                            </Typography>
                            <Typography variant="body2">
                                By saving to cloud, you agree to JourneyBot&rsquo;s{' '}
                                <a href="/terms">Terms of Use</a> and acknowledge our{' '}
                                <a href="/privacy">Privacy Policy</a>.
                            </Typography>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleKeepLocalOnly} color="primary" data-testid="keep-local-only">
                            Keep local only
                        </Button>
                        <Button onClick={handleAcceptCloudSaveNotice} variant="contained" color="primary" data-testid="confirm-save-cloud">
                            Save to cloud
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </Box>
    )

}

export default GroupedTasks
